import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export enum DisplayOrientation {
  LANDSCAPE = 0,
  PORTRAIT = 1,
}

export enum DisplayType {
  WEB = 0,
  HANDHELD = 1,
  TABLET = 2,
}

@Injectable({
  providedIn: 'root',
})
export class DisplayService {
  private screenOrientation$: BehaviorSubject<DisplayOrientation> = new BehaviorSubject<DisplayOrientation>(
    DisplayOrientation.LANDSCAPE,
  );

  private screenType$: BehaviorSubject<DisplayType> = new BehaviorSubject<DisplayType>(DisplayType.WEB);

  private destroyed$: Subject<void> = new Subject<void>();

  private _isSubnavRoute: boolean = false;

  screenOrientationInstant: DisplayOrientation = DisplayOrientation.LANDSCAPE;

  screenTypeInstant: DisplayType = DisplayType.WEB;

  constructor(private responsive: BreakpointObserver) {
    this.responsive
      .observe([
        Breakpoints.HandsetLandscape,
        Breakpoints.HandsetPortrait,
        Breakpoints.TabletLandscape,
        Breakpoints.TabletPortrait,
        Breakpoints.WebLandscape,
        Breakpoints.WebPortrait,
      ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result.matches) {
          const matches = Object.entries(result.breakpoints)
            .map(([key, value]) => ({ key, value }))
            .filter((b) => b.value === true);

          if (matches.length > 0) {
            // There should only ever be one match.
            const match = matches[0];

            switch (match.key) {
              case Breakpoints.HandsetLandscape:
                this.screenType$.next(DisplayType.HANDHELD);
                this.screenTypeInstant = DisplayType.HANDHELD;
                this.screenOrientation$.next(DisplayOrientation.LANDSCAPE);
                this.screenOrientationInstant = DisplayOrientation.LANDSCAPE;
                break;
              case Breakpoints.HandsetPortrait:
                this.screenType$.next(DisplayType.HANDHELD);
                this.screenTypeInstant = DisplayType.HANDHELD;
                this.screenOrientation$.next(DisplayOrientation.PORTRAIT);
                this.screenOrientationInstant = DisplayOrientation.PORTRAIT;
                break;
              case Breakpoints.TabletLandscape:
                this.screenType$.next(DisplayType.TABLET);
                this.screenTypeInstant = DisplayType.TABLET;
                this.screenOrientation$.next(DisplayOrientation.LANDSCAPE);
                this.screenOrientationInstant = DisplayOrientation.LANDSCAPE;
                break;
              case Breakpoints.TabletPortrait:
                this.screenType$.next(DisplayType.TABLET);
                this.screenTypeInstant = DisplayType.TABLET;
                this.screenOrientation$.next(DisplayOrientation.PORTRAIT);
                this.screenOrientationInstant = DisplayOrientation.PORTRAIT;
                break;
              case Breakpoints.WebLandscape:
              default:
                this.screenType$.next(DisplayType.WEB);
                this.screenTypeInstant = DisplayType.WEB;
                this.screenOrientation$.next(DisplayOrientation.LANDSCAPE);
                this.screenOrientationInstant = DisplayOrientation.LANDSCAPE;
                break;
              case Breakpoints.WebPortrait:
                this.screenType$.next(DisplayType.WEB);
                this.screenTypeInstant = DisplayType.WEB;
                this.screenOrientation$.next(DisplayOrientation.PORTRAIT);
                this.screenOrientationInstant = DisplayOrientation.PORTRAIT;
                break;
            }
          }
        }
      });
  }

  get isHandheld(): boolean {
    return this.screenTypeInstant === DisplayType.HANDHELD;
  }

  get isTablet(): boolean {
    return this.screenTypeInstant === DisplayType.TABLET;
  }

  get isWeb(): boolean {
    return this.screenTypeInstant === DisplayType.WEB;
  }

  get isLandscape(): boolean {
    return this.screenOrientationInstant === DisplayOrientation.LANDSCAPE;
  }

  get isPortrait(): boolean {
    return this.screenOrientationInstant === DisplayOrientation.PORTRAIT;
  }

  get isSubnavRoute(): boolean {
    return this._isSubnavRoute;
  }

  set isSubnavRoute(val: boolean) {
    this._isSubnavRoute = val;
  }

  get screenOrientation(): Observable<DisplayOrientation> {
    return this.screenOrientation$.asObservable();
  }

  get screenType(): Observable<DisplayType> {
    return this.screenType$.asObservable();
  }
}
